import { db } from "@/plugins/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  onSnapshot,
} from "firebase/firestore";

/**
 * Streaming Service - Manages streaming server configurations and pilot assignments
 */

/**
 * Build RTMP URL from streaming configuration
 * @param {Object} streamingConfig - Streaming config object
 * @param {Object} serverData - Server data object
 * @returns {string} RTMP URL
 */
function buildRTMPUrl(streamingConfig, serverData) {
  if (!streamingConfig || !serverData) {
    return null;
  }
  const { assignedKey } = streamingConfig;
  const { ipAddress, rtmpPort } = serverData;
  return `rtmp://${ipAddress}:${rtmpPort}/app/${assignedKey}`;
}

/**
 * Build WebRTC URL from streaming configuration
 * @param {Object} streamingConfig - Streaming config object
 * @param {Object} serverData - Server data object
 * @returns {string} WebRTC URL
 */
function buildWebRTCUrl(streamingConfig, serverData) {
  if (!streamingConfig || !serverData) {
    return null;
  }
  const { assignedKey } = streamingConfig;
  const { webrtcDomain, webrtcPort } = serverData;
  // Use domain if available, otherwise use IP
  const host = webrtcDomain || serverData.ipAddress;
  return `wss://${host}:${webrtcPort}/app/${assignedKey}`;
}

/**
 * Get all active streaming servers
 * @returns {Promise<Array>} Array of server documents
 */
export async function getAvailableServers() {
  try {
    const serversQuery = query(
      collection(db, "streaming-servers"),
      where("status", "==", "active")
    );
    const querySnapshot = await getDocs(serversQuery);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error fetching available servers:", error);
    throw error;
  }
}

/**
 * Get server availability (count of available keys)
 * @param {string} serverId - Server document ID
 * @returns {Promise<Object>} Object with available count and total count
 */
export async function getServerAvailability(serverId) {
  try {
    const serverDoc = await getDoc(doc(db, "streaming-servers", serverId));
    if (!serverDoc.exists()) {
      throw new Error("Server not found");
    }
    const serverData = serverDoc.data();
    const streamKeys = serverData.streamKeys || {};
    const availableCount = Object.values(streamKeys).filter(
      (value) => value === null
    ).length;
    const totalCount = Object.keys(streamKeys).length;
    return {
      available: availableCount,
      total: totalCount,
      server: { id: serverDoc.id, ...serverData },
    };
  } catch (error) {
    console.error("Error getting server availability:", error);
    throw error;
  }
}

/**
 * Assign a stream key to a pilot (or auto-assign if streamKey not provided)
 * @param {string} userId - Pilot user ID
 * @param {string} serverId - Server document ID
 * @param {string} [streamKey] - Optional stream key to assign. If not provided, auto-assigns first available key
 * @returns {Promise<string>} The assigned stream key
 */
export async function assignStreamToUser(userId, serverId, streamKey = null) {
  try {
    const response = await fetch(
      // `http://localhost:3001/assign-stream`,
      `https://skyhost.ai/assign-stream`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId, serverId, streamKey }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to assign stream: ${response.status}`);
    }

    const result = await response.json();

    if (!result.status || !result.data) {
      throw new Error(result.message || "Failed to assign stream key");
    }

    return result.data.assignedKey;
  } catch (error) {
    console.error("Error assigning stream to user:", error);
    throw error;
  }
}

/**
 * Create a new streaming server
 * @param {Object} serverData - Server configuration object
 * @returns {Promise<string>} Document ID of created server
 */
export async function createStreamingServer(serverData) {
  try {
    const response = await fetch(
      //`http://localhost:3001/create-streaming-server`,
      `https://skyhost.ai/create-streaming-server`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(serverData),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to create server: ${response.status}`);
    }

    const result = await response.json();

    if (!result.status || !result.data) {
      throw new Error(result.message || "Failed to create streaming server");
    }

    return result.data.serverId;
  } catch (error) {
    console.error("Error creating streaming server:", error);
    throw error;
  }
}

/**
 * Subscribe to real-time updates of user's streaming URLs
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function that receives streaming URLs or null
 * @returns {Function} Unsubscribe function to stop listening
 */
export function subscribeToUserStreamingUrls(userId, callback) {
  if (!userId) {
    console.warn("No userId provided to subscribeToUserStreamingUrls");
    callback(null);
    return () => {};
  }

  const usersQuery = query(
    collection(db, "users"),
    where("userId", "==", userId)
  );

  const unsubscribe = onSnapshot(
    usersQuery,
    async (querySnapshot) => {
      try {
        if (querySnapshot.empty) {
          callback(null);
          return;
        }

        const userData = querySnapshot.docs[0].data();
        const streamingConfig = userData.streamingConfig || null;

        if (!streamingConfig || !streamingConfig.serverId) {
          callback(null);
          return;
        }

        // Fetch server data
        const serverDoc = await getDoc(
          doc(db, "streaming-servers", streamingConfig.serverId)
        );

        if (!serverDoc.exists()) {
          callback(null);
          return;
        }

        const serverData = serverDoc.data();
        callback({
          rtmpUrl: buildRTMPUrl(streamingConfig, serverData),
          webrtcUrl: buildWebRTCUrl(streamingConfig, serverData),
          config: streamingConfig,
          server: { id: serverDoc.id, ...serverData },
        });
      } catch (error) {
        console.error("Error in streaming URL subscription:", error);
        callback(null);
      }
    },
    (error) => {
      console.error("Error subscribing to user streaming URLs:", error);
      callback(null);
    }
  );

  return unsubscribe;
}
